// @flow
import React, { Component } from 'react';
import FacebookEmailCollectionForm from 'app/common/react/components/growth/unauth/signup/FacebookEmailCollection/FacebookEmailCollectionForm';
import i18n from 'app/common/lib/i18n';
import loginUtil from 'app/www-unauth/lib/loginUtil';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import runValidators from 'app/common/react/components/growth/unauth/lib/ValidationUtils';
import trackRegisterAction from 'app/common/lib/util/trackRegisterAction';
import UserRegistration from 'app/common/react/components/growth/unauth/signup/UserRegistration';
import { Email } from 'app/common/lib/react/validator';
import { type UnauthSeoExperiments } from 'app/common/lib/react/withUnauthSeoExperiments';
import {
  type FBRegistrationCredentialsType,
  registrationRequiresAgeGDPR,
} from 'app/packages/pinterest-authentication/registration';
import {
  withRequestContext,
  type RequestContextType,
} from 'app/common/react/components/RequestContext';

const styles = {
  container: {
    margin: '0 auto',
    width: '268px',
  },
};

const USER_INFO_TYPE = {
  EMAIL: 'email',
};

type Props = {
  nextUrlParam?: string,
  loadingIndicatorCallback: (boolean) => void,
  showEmailAlreadyExistModal: (
    type: string,
    userData: Object,
    credentialsAndLoggingData: Object,
  ) => void,
  onNotNowClick: () => void,
  showSocialSignupWithAgeStep: (
    type: string,
    userData: Object,
    credentialsAndLoggingData: Object,
  ) => void,
  registrationCredentials: FBRegistrationCredentialsType,
  seoUnauthExperiments: UnauthSeoExperiments,
  requestContext: RequestContextType,
};

type State = {
  email: string,
  emailError: string,
};

class FacebookEmailCollection extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      email: '',
      emailError: '',
    };
  }

  componentDidMount() {
    this.mounted = true;

    if (this.props.loadingIndicatorCallback) {
      this.props.loadingIndicatorCallback(false);
    }
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  mounted = false;

  handleEmailChange = ({ value }: { value: string }) => {
    this.setState({
      email: value,
    });
  };

  showError = (errorMessage: string, userInfoType: string) => {
    if (!this.mounted) {
      return;
    }

    if (userInfoType === USER_INFO_TYPE.EMAIL) {
      this.setState({ emailError: errorMessage });
    }
  };

  handleSubmit = () => {
    trackRegisterAction('unauth.fb_phone_only_signup.submit_email');
    if (this.state.email) {
      trackRegisterAction('unauth.fb_phone_only_signup.submit_email');
      this.attemptEmailSignupOrLogin();
    } else {
      const emailError = i18n._('Email is required', 'email error message');
      this.setState({ emailError });
    }
  };

  handleSignupSuccess = () => {
    loginUtil.handleSignupRedirect(this.props.nextUrlParam || window.location.pathname, true);
    trackRegisterAction('unauth.fb_phone_only_signup.signup_success');
  };

  handleSignupFailure = (userInfoType: string) => (error: { message?: string }) => {
    const errorMessage =
      error.message ||
      i18n._(
        'Oops! Something went wrong… Click Continue to try again.',
        'Error during signup during FB email collection step',
      );
    this.showError(errorMessage, userInfoType);
    trackRegisterAction('unauth.fb_phone_only_signup.signup_failure');
  };

  attemptEmailSignupOrLogin = () => {
    const { country } = this.props.requestContext;
    const { email } = this.state;
    const emailValidators = [
      Email({
        message: i18n._("Hmm... that doesn't look like an email address.", 'email error message'),
      }),
    ];
    const emailValidationError = runValidators(email, emailValidators);
    if (emailValidationError) {
      trackRegisterAction('unauth.fb_phone_only_signup.email_validation_failure');
      this.setState({ emailError: emailValidationError });
      return;
    }
    const {
      registrationCredentials: { type, credentialsAndLoggingData },
      showSocialSignupWithAgeStep,
      showEmailAlreadyExistModal,
    } = this.props;
    const facebook_token = credentialsAndLoggingData.facebook_token
      ? credentialsAndLoggingData.facebook_token
      : '';
    const facebook_id = credentialsAndLoggingData.facebook_id
      ? credentialsAndLoggingData.facebook_id
      : '';
    const userData = {
      email,
      facebook_token,
      facebook_id,
    };
    const userExistsResource = ResourceFactory.create('EmailExistsResource', {
      email,
    });
    userExistsResource.callGet().then(
      (resourceResponse) => {
        if (resourceResponse.resource_response.data === false) {
          if (registrationRequiresAgeGDPR(country)) {
            showSocialSignupWithAgeStep(type, userData, credentialsAndLoggingData);
          } else {
            UserRegistration.attemptFacebookRegistration(
              {
                ...userData,
              },
              true,
            ).then(this.handleSignupSuccess, this.handleSignupFailure(USER_INFO_TYPE.EMAIL));
          }
        } else {
          showEmailAlreadyExistModal(type, userData, credentialsAndLoggingData);
          trackRegisterAction('unauth.fb_phone_only_signup.email_existed');
        }
      },
      () => {
        const errorMessage = i18n._(
          "Hmm... that doesn't look like an email address.",
          'email error message',
        );
        this.showError(errorMessage, USER_INFO_TYPE.EMAIL);
        trackRegisterAction('unauth.fb_phone_only_signup.email_exist_api_call_failure');
      },
    );
  };

  render() {
    return (
      <div style={styles.container}>
        <FacebookEmailCollectionForm
          email={this.state.email}
          emailError={this.state.emailError}
          handleEmailChange={this.handleEmailChange}
          handleSubmit={this.handleSubmit}
          onNotNowClick={this.props.onNotNowClick}
          seoUnauthExperiments={this.props.seoUnauthExperiments}
        />
      </div>
    );
  }
}

export default withRequestContext(FacebookEmailCollection);
